import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Phone, User, Mail, Clock, CalendarCheck } from "lucide-react";
import { insertScheduleCallSchema, type InsertScheduleCall } from "@/lib/schema";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface ScheduleCallFormProps {
  onSuccess?: () => void;
  showHeader?: boolean;
  className?: string;
}

export default function ScheduleCallForm({
  onSuccess,
  showHeader = true,
  className
}: ScheduleCallFormProps) {
  const { toast } = useToast();

  const form = useForm({
    resolver: zodResolver(insertScheduleCallSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      preferred_time: "",
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: async (data: any) => {
      const formData = {
        ...data,
        email: data.email || null,
        preferred_time: data.preferred_time || null,
      };

      console.log('Datos a enviar:', formData);

      // NOTA: Esta llamada directa a la Edge Function puede causar errores de CORS.
      // Para solucionarlo, tienes dos opciones:
      // 1. Configurar CORS en tu Edge Function de Supabase (recomendado)
      // 2. Crear un endpoint proxy en tu API local que reenvíe la solicitud
      const response = await fetch("https://jlufgkgssnxumguvknhc.functions.supabase.co/schedule-call", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.message || "Error al agendar la llamada");
      }

      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "¡Solicitud enviada!",
        description: "Nos pondremos en contacto contigo pronto.",
      });
      form.reset();
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Hubo un problema al enviar tu solicitud. Por favor, intenta nuevamente.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: any) => {
    scheduleMutation.mutate(data);
  };

  return (
    <div className={cn(
      "bg-[#111] p-6 w-full",
      className
    )}>
      {showHeader && (
        <div className="flex flex-col items-center text-center mb-8">
          <div className="bg-[#FCAA0D]/20 p-4 rounded-full mb-4">
            <CalendarCheck className="w-8 h-8 text-[#FCAA0D]" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Agenda tu llamada</h2>
          <p className="text-gray-300 max-w-md text-sm">
            Déjanos tus datos y nos pondremos en contacto contigo lo antes posible
          </p>
        </div>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <div className="grid gap-5 md:grid-cols-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/90 flex items-center gap-2 text-sm font-medium mb-1.5">
                    <User className="w-4 h-4 text-[#FCAA0D]" />
                    Nombre
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Tu nombre"
                      {...field}
                      className="bg-black border-white/10 focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50
                                text-white placeholder:text-white/40 h-11"
                    />
                  </FormControl>
                  <FormMessage className="text-xs mt-1" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/90 flex items-center gap-2 text-sm font-medium mb-1.5">
                    <Phone className="w-4 h-4 text-[#FCAA0D]" />
                    Teléfono
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="+34 XXX XXX XXX"
                      {...field}
                      className="bg-black border-white/10 focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50
                                text-white placeholder:text-white/40 h-11"
                    />
                  </FormControl>
                  <FormMessage className="text-xs mt-1" />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/90 flex items-center gap-2 text-sm font-medium mb-1.5">
                  <Mail className="w-4 h-4 text-[#FCAA0D]" />
                  Email <span className="text-white/50 text-xs">(opcional)</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="tu@email.com"
                    {...field}
                    className="bg-black border-white/10 focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50
                              text-white placeholder:text-white/40 h-11"
                  />
                </FormControl>
                <FormMessage className="text-xs mt-1" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="preferred_time"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/90 flex items-center gap-2 text-sm font-medium mb-1.5">
                  <Clock className="w-4 h-4 text-[#FCAA0D]" />
                  Horario preferido <span className="text-white/50 text-xs">(opcional)</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Ej: Mañanas, tardes..."
                    {...field}
                    className="bg-black border-white/10 focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50
                              text-white placeholder:text-white/40 h-11"
                  />
                </FormControl>
                <FormMessage className="text-xs mt-1" />
              </FormItem>
            )}
          />
          <div className="flex justify-center pt-4">
            <Button
              type="submit"
              className="w-full font-montserrat uppercase tracking-wide text-sm font-bold
                        bg-[#FCAA0D] hover:bg-[#FCAA0D]/90
                        text-black h-12"
              disabled={scheduleMutation.isPending}
            >
              {scheduleMutation.isPending ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-black/20 border-t-black/80 rounded-full animate-spin" />
                  <span>Enviando...</span>
                </div>
              ) : (
                "Enviar solicitud"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
